import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { Artifact, Photo } from "./contracts";

const scoreLabel = {
  model_self_report: "Model score (uncalibrated)",
  calibrated: "Calibrated score",
  human_assertion: "Human assertion",
};
export function PhotoEvidence({
  photo,
  onArtifact,
  onReference,
}: {
  photo: Photo;
  onArtifact: (artifact: Artifact) => void;
  onReference?: (assetId: string) => void;
}) {
  const candidates = [...photo.candidates].sort((a, b) => b.score - a.score);
  const margin =
    candidates.length > 1 ? candidates[0].score - candidates[1].score : null;
  return (
    <article
      className="space-y-3 rounded-md border p-3"
      aria-label={photo.filename}
    >
      <div className="flex flex-wrap items-center gap-2">
        <strong>{photo.filename}</strong>
        <Badge variant="outline">{photo.status.replaceAll("_", " ")}</Badge>
      </div>
      {photo.observation?.thumbnail ? (
        <img
          src={photo.observation.thumbnail.url}
          alt={`Room evidence: ${photo.filename}`}
          className="max-h-64 max-w-full rounded object-contain"
          loading="lazy"
          referrerPolicy="no-referrer"
        />
      ) : null}
      <dl className="grid gap-2 text-sm sm:grid-cols-2">
        <div>
          <dt>Assigned room</dt>
          <dd>{photo.plan_room_id ?? "Unknown"}</dd>
        </div>
        <div>
          <dt>Camera room</dt>
          <dd>{photo.camera_room_id ?? "Unknown"}</dd>
        </div>
        <div>
          <dt>{scoreLabel[photo.confidence_kind]}</dt>
          <dd>
            {photo.confidence_kind === "human_assertion"
              ? "Room label only; passage is unverified"
              : `${photo.score / 100}%`}
          </dd>
        </div>
        <div>
          <dt>Candidate margin</dt>
          <dd>
            {margin === null ? "Not available" : `${margin / 100} points`}
          </dd>
        </div>
      </dl>
      {photo.visible_room_ids.length ? (
        <p className="text-sm">
          Visible rooms: {photo.visible_room_ids.join(", ")}
        </p>
      ) : null}
      <p className="text-xs text-muted-foreground">
        Selected references:{" "}
        {photo.reference_asset_ids.length
          ? photo.reference_asset_ids.map((asset) => (
              <Button
                key={asset}
                size="sm"
                variant="link"
                onClick={() => onReference?.(asset)}
                disabled={!onReference}
              >
                Inspect {asset}
              </Button>
            ))
          : "None"}
      </p>
      <ul className="space-y-2 text-sm">
        {candidates.map((candidate) => (
          <li
            key={candidate.room_id ?? "unknown"}
            className="rounded bg-muted/40 p-2"
          >
            <strong>{candidate.room_id ?? "Unknown / no matching room"}</strong>{" "}
            · {candidate.score / 100}%<p>{candidate.reason}</p>
            <p className="text-xs">
              Support: {candidate.supports.join(", ") || "None"}
            </p>
            {candidate.contradictions.length ? (
              <p className="text-destructive">
                Contradictions: {candidate.contradictions.join(", ")}
              </p>
            ) : null}
          </li>
        ))}
      </ul>
      {photo.observation ? (
        <details>
          <summary>Structural observations ({photo.observation.view})</summary>
          <ul className="space-y-1 text-sm">
            {photo.observation.evidence.map((item) => (
              <li key={item.evidence_id}>
                <Badge variant="outline">
                  {item.source} · {item.state}
                </Badge>{" "}
                {item.feature}: {item.description}
              </li>
            ))}
          </ul>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onArtifact(photo.observation!.artifact)}
          >
            Inspect observation JSON
          </Button>
        </details>
      ) : (
        <p className="text-sm">Observation unavailable.</p>
      )}
    </article>
  );
}
